/**
 * Caja de busqueda de la cabecera, con sus sugerencias (BUS-01, BUS-02).
 *
 * Es un `<form>` de verdad con un `<input type="search">`: se envia con Enter,
 * el teclado del celular muestra la tecla "buscar" y el navegador lo reconoce
 * como buscador del sitio. Un `div` con `onClick` no haria nada de eso.
 *
 * Al lado va el acceso a buscar por foto (BUS-03), para quien tiene la pieza en
 * la mano y no sabe como se llama — que es justo el caso en el que esta caja no
 * le sirve de nada. Abre un panel colgado de la caja (`PanelBusquedaFoto`), en
 * el mismo lugar donde salen las sugerencias, y no una pantalla aparte
 * (rediseño del 14-09-2026, con lowes.com como referencia): elegir la foto,
 * recortarla y ver los parecidos pasa todo ahi, sin irse de la pagina que se
 * estaba mirando. Los dos paneles no conviven: abrir uno cierra el otro.
 *
 * ESTE ARCHIVO MANEJA EL TECLADO Y EL FOCO; el panel solo pinta. Estan separados
 * porque quien tiene el input es quien puede saber que fila esta marcada, y
 * `aria-activedescendant` tiene que vivir en el input aunque el elemento que
 * nombra viva en el panel.
 *
 * Para un lector de pantalla esto es un `combobox` con una lista abajo: el input
 * anuncia si esta abierta, cuantas opciones hay y cual esta marcada, sin que el
 * foco se mueva nunca del input — que es lo que permite seguir escribiendo
 * mientras se recorre la lista con las flechas.
 */
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Icono } from '../comunes/Icono';
import { PanelBusquedaFoto } from '../busqueda-foto/PanelBusquedaFoto';
import { useSugerencias } from '../../hooks/use-sugerencias';
import { alPedirBusquedaPorFoto } from '../../utilidades/busqueda-foto-atajo';
import { rutas } from '../../utilidades/rutas';
import { opcionesDe } from '../../utilidades/sugerencias';
import { PanelSugerencias } from './PanelSugerencias';

export interface BuscadorProps {
  /** Unico en la pagina: es el `for` de su etiqueta. */
  id: string;
  marcador: string;
  /** Termino con el que llega la caja, al volver a una busqueda. */
  inicial?: string;
  /**
   * Solo el buscador de la cabecera: responde al atajo `pedirBusquedaPorFoto`
   * que dispara la pagina de resultados sin nada. Si lo escucharan los dos
   * buscadores de esa pagina, se abririan dos paneles a la vez.
   */
  atajoFoto?: boolean;
}

/** Ninguna fila marcada. Escribir siempre vuelve a este estado. */
const NINGUNA = -1;

/**
 * Largo maximo de lo que se busca. El backend corta en el mismo numero
 * (`LARGO_MAXIMO_TERMINO` en `busqueda.controlador.ts`): si cambia uno, cambia
 * el otro.
 */
const LARGO_MAXIMO_TERMINO = 100;

export function Buscador({ id, marcador, inicial = '', atajoFoto = false }: BuscadorProps) {
  const [termino, setTermino] = useState(inicial);
  const [enfocado, setEnfocado] = useState(false);
  const [marcada, setMarcada] = useState(NINGUNA);
  const [fotoAbierta, setFotoAbierta] = useState(false);
  const contenedor = useRef<HTMLElement>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const navegar = useNavigate();

  const idPanel = useId();
  const idPanelFoto = `${idPanel}-foto`;
  const idOpcion = (indice: number) => `${idPanel}-opcion-${String(indice)}`;

  // Con el panel de la foto abierto no se piden sugerencias: los dos paneles
  // cuelgan del mismo lugar y no pueden verse a la vez.
  const sugerencias = useSugerencias(termino, enfocado && !fotoAbierta);
  const opciones = sugerencias !== null ? opcionesDe(sugerencias) : [];
  const abierto = enfocado && !fotoAbierta && sugerencias !== null;

  /*
   * Cerrar al tocar fuera, cualquiera de los dos paneles.
   *
   * Va con `pointerdown` en el documento y no con el `blur` del input: con
   * `blur` el panel se cierra ANTES de que el clic llegue a la fila, y elegir
   * una sugerencia con el mouse no funcionaria nunca.
   */
  useEffect(() => {
    if (!abierto && !fotoAbierta) return;

    const alTocar = (evento: PointerEvent) => {
      const dentro = contenedor.current?.contains(evento.target as Node) ?? false;
      if (dentro) return;
      setEnfocado(false);
      setFotoAbierta(false);
    };

    document.addEventListener('pointerdown', alTocar);
    return () => {
      document.removeEventListener('pointerdown', alTocar);
    };
  }, [abierto, fotoAbierta]);

  // Escape cierra el panel de la foto desde cualquier parte de el: adentro hay
  // botones y un marco de recorte, no solo el input.
  useEffect(() => {
    if (!fotoAbierta) return;

    const alPresionar = (evento: globalThis.KeyboardEvent) => {
      if (evento.key === 'Escape') setFotoAbierta(false);
    };
    document.addEventListener('keydown', alPresionar);
    return () => {
      document.removeEventListener('keydown', alPresionar);
    };
  }, [fotoAbierta]);

  useEffect(() => {
    if (!atajoFoto) return;
    return alPedirBusquedaPorFoto(() => {
      setEnfocado(false);
      setFotoAbierta(true);
      contenedor.current?.scrollIntoView({ block: 'nearest' });
    });
  }, [atajoFoto]);

  function cerrar(): void {
    setEnfocado(false);
    setMarcada(NINGUNA);
  }

  /**
   * Cierra y suelta el foco. Solo al irse a otra pagina: en el telefono, con
   * el foco en la caja, el teclado se quedaba abierto tapando los resultados
   * (ni enviar el formulario ni tocar un enlace lo sueltan). Escape no lo usa:
   * con teclado, el foco tiene que quedarse en la caja.
   */
  function cerrarYSoltar(): void {
    cerrar();
    entrada.current?.blur();
  }

  function enviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();

    // Con una fila marcada, Enter va a esa fila y no a la pagina de resultados:
    // es el atajo que la historia pide, y llegar a la ficha en un paso es
    // exactamente lo que hace util el panel.
    const elegida = opciones[marcada];
    if (elegida !== undefined) {
      cerrarYSoltar();
      navegar(elegida.destino);
      return;
    }

    const limpio = termino.trim();
    if (limpio.length === 0) return;
    cerrarYSoltar();
    navegar(rutas.busqueda(limpio));
  }

  function alTeclear(evento: KeyboardEvent<HTMLInputElement>): void {
    if (evento.key === 'Escape') {
      cerrar();
      return;
    }

    if (evento.key !== 'ArrowDown' && evento.key !== 'ArrowUp') return;
    if (!abierto || opciones.length === 0) return;

    // Sin esto la flecha mueve el cursor dentro del texto escrito.
    evento.preventDefault();

    // Da la vuelta en los dos extremos: bajar desde la ultima lleva a la
    // primera, y subir desde "ninguna" lleva a la ultima.
    const ultima = opciones.length - 1;
    setMarcada((actual) => {
      if (evento.key === 'ArrowDown') return actual >= ultima ? NINGUNA : actual + 1;
      return actual <= NINGUNA ? ultima : actual - 1;
    });
  }

  return (
    <search className={`buscador${abierto || fotoAbierta ? ' abierto' : ''}`} ref={contenedor}>
      <form onSubmit={enviar} role="search">
        <label className="solo-lectores" htmlFor={id}>
          Buscar productos en el catálogo
        </label>
        <div className="buscador-caja">
          <Icono nombre="buscar" />
          <input
            ref={entrada}
            id={id}
            type="search"
            name="termino"
            maxLength={LARGO_MAXIMO_TERMINO}
            autoComplete="off"
            placeholder={marcador}
            value={termino}
            onChange={(evento) => {
              setTermino(evento.target.value);
              // Lo que estaba marcado ya no existe: la lista es otra.
              setMarcada(NINGUNA);
              setEnfocado(true);
            }}
            onFocus={() => {
              setEnfocado(true);
            }}
            onKeyDown={alTeclear}
            role="combobox"
            aria-expanded={abierto}
            aria-controls={idPanel}
            aria-autocomplete="list"
            {...(marcada >= 0 ? { 'aria-activedescendant': idOpcion(marcada) } : {})}
          />
          {termino.length > 0 ? (
            <button
              type="button"
              className="buscador-limpiar"
              onClick={() => {
                setTermino('');
                setMarcada(NINGUNA);
              }}
              aria-label="Limpiar la búsqueda"
            >
              <Icono nombre="cerrar" />
            </button>
          ) : null}
          <button
            type="button"
            className={fotoAbierta ? 'buscador-foto abierto' : 'buscador-foto'}
            title="Buscar por foto"
            aria-label="Buscar por foto"
            aria-expanded={fotoAbierta}
            aria-controls={idPanelFoto}
            onClick={() => {
              setEnfocado(false);
              setFotoAbierta((valor) => !valor);
            }}
          >
            <Icono nombre="camara" />
            <span className="buscador-foto-texto" aria-hidden="true">
              Foto
            </span>
          </button>
          <button type="submit" className="buscador-ir">
            Buscar
          </button>
        </div>
      </form>

      {fotoAbierta ? (
        <PanelBusquedaFoto id={idPanelFoto} onCerrar={() => setFotoAbierta(false)} />
      ) : null}

      {abierto && sugerencias !== null ? (
        <PanelSugerencias
          sugerencias={sugerencias}
          escrito={termino}
          id={idPanel}
          marcada={marcada}
          idOpcion={idOpcion}
          alElegir={cerrarYSoltar}
        />
      ) : null}
    </search>
  );
}
